// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import { Box, Button, Dialog, IconButton, Toolbar, Typography } from '@mui/material';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { TextInputLabel } from '../../../components/Form/TextInput';
import PartyDisplay from '../../../components/PartyDisplay';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useAllocations from '../../../hooks/queries/registry/tokenStandard/useAllocations';
import { fmt } from '../../../utils/common';
import { Position } from './Positions';
import { InstructedRow } from './types';

interface InstructDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  row?: InstructedRow;
  onSubmit: (r: InstructedRow) => Promise<void>;
}

export const InstructDetailsDialog: React.FC<InstructDetailsDialogProps> = ({
  open,
  onClose,
  onSubmit,
  row,
}) => {
  const nav = useNavigate();
  const party = useModuleParty();
  const isPledgor = !!row?.payload.positions.filter((p) => p.pledgor === party)?.length;

  const goToAllocations = () => {
    nav('/registry/allocationRequest');
  };
  const currTime = new Date();
  const settleBefore = row?.payload.settleBefore;
  const settleBeforeDate = new Date(settleBefore || '');
  const passedSettleTime = currTime > settleBeforeDate;

  const allocations = useAllocations();
  const relevantAllocations = allocations.data
    ?.filter(
      (a) =>
        a.view(Allocation).allocation.settlement.settlementRef.id ===
        `${row?.payload.agreementId};${row?.payload.id}`,
    )
    .sort((a, b) =>
      a
        .view(Allocation)
        .allocation.transferLegId.localeCompare(b.view(Allocation).allocation.transferLegId),
    );

  const allocationCids = relevantAllocations?.map(
    (a) => a.contractId as unknown as ContractId<Allocation>,
  );
  const isFullyAllocated = (allocationCids?.length || 0) === row?.payload.positions.length;
  if (!row) {
    return null;
  }
  return (
    <Dialog open={open} onClose={onClose}>
      <Toolbar>
        <Typography fontWeight="bold">Instruct Detail</Typography>
        <IconButton size="small" onClick={onClose} sx={{ ml: 'auto' }}>
          <Close fontSize="small" color="action" />
        </IconButton>
      </Toolbar>

      <Box sx={{ minWidth: '600px', p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Agreement ID" />
          <Typography variant="body2">{row.payload.agreementId}</Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Instruct ID" />
          <Typography variant="body2">{row.payload.instructId || '--'}</Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Party A" />
          <Typography variant="body2">
            <PartyDisplay party={row.payload.partyA} />
          </Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Party B" />
          <Typography variant="body2">
            <PartyDisplay party={row.payload.partyB} />
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {row.payload.allocateBefore && (
            <Box sx={{ flex: 1, mb: 2 }}>
              <TextInputLabel label="Allocate Before" />
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography fontWeight="bold" variant="body2">
                  {new Date(row.payload.allocateBefore).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  local time
                </Typography>
              </Box>
            </Box>
          )}
          {row.payload.settleBefore && (
            <Box sx={{ flex: 1, mb: 2 }}>
              <TextInputLabel label="Settle Before" />
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography fontWeight="bold" variant="body2">
                  {new Date(row.payload.settleBefore).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  local time
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Positions" />
          {row.payload.positions.map((position) => (
            <Position
              key={position.instrument.id}
              amount={fmt(position.amount, 2)}
              instrumentId={position.instrument.id}
              isPledgor={position.pledgor === party}
            />
          ))}
        </Box>
        {isPledgor && !isFullyAllocated && (
          <Box>
            <TextInputLabel label="Message" />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="error">
                Awaiting allocation of all positions
              </Typography>
              <Button size="small" onClick={goToAllocations} sx={{ ml: 1 }} variant="text">
                Go to allocations
              </Button>
            </Box>
          </Box>
        )}
        {passedSettleTime && (
          <Box>
            <TextInputLabel label="Message" />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="error">
                Settlement deadline has passed. Please cancel this instruction and create a new one.
              </Typography>
            </Box>
          </Box>
        )}
        {!isPledgor && (
          <Box>
            <TextInputLabel label="Message" />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">Awaiting settlement by counterparty.</Typography>
            </Box>
          </Box>
        )}
        {isPledgor && (
          <Box
            sx={{ mt: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
            <AsyncButton
              disabled={!isFullyAllocated}
              onClick={async () => {
                await onSubmit(row);
              }}
              variant="contained"
            >
              Settle
            </AsyncButton>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
